import React from "react";
import Joi from "joi-browser";
import uuid from "react-uuid";
import { toast } from "react-toastify";
import { Tabs, Tab } from "react-bootstrap";
import {
  getMarketControl,
  getMarketControlByMarket,
  insertOrUpdateMarketControl,
} from "../../../common/services/tradingEngine/marketStateService";
import { getMarkets } from "../../../common/services/markets/marketService";
import Form from "../../../common/components/form/form";
import Loading from "../../../common/components/loading/loading";
import TradingMarketOrganizations from "./tradingMarketOrganizations";
import TradingMarketNegotiationMetadata from "./tradingMarketNegotiationMetadata";
import { withTranslation } from "react-i18next";

class TradingMarket extends Form {
  state = {
    data: {
      marketId: "",
      halted: "",
      allowPick: "",
      allowAllOrNone: "",
      allowFillOrKill: "",
      withdrawOnCrossTrade: "",
      applyCounterpartyWhitelist: "",
      active: "",
      eventId: "",
    },
    organizationIds: [],
    createdDate: "",
    updatedDate: "",
    errors: {},
    isLoading: true,
    isNew: true,
    markets: [],
  };

  schema = {
    marketId: Joi.string().required(),
    halted: Joi.boolean().required().label(this.props.t("Hatled")),
    allowPick: Joi.boolean().required().label(this.props.t("AllowPick")),
    allowAllOrNone: Joi.boolean().required().label(this.props.t("AllowAllOrNone")),
    allowFillOrKill: Joi.boolean().required().label(this.props.t("AllowFillOrKill")),
    withdrawOnCrossTrade: Joi.boolean().required().label(this.props.t("WithdrawOnCrossTrade")),
    applyCounterpartyWhitelist: Joi.boolean().required().label(this.props.t("ApplyCounterpartyWhitelist")),
    active: Joi.boolean().label(this.props.t("Active")),
    eventId: Joi.string(),
  };

  async componentDidMount() {
    const { marketId, isNew } = this.props;
    this.setState({ isNew: isNew, tabId: marketId });

    const [markets, tradingMarketControls] = await Promise.all([
      getMarkets(),
      getMarketControl(),
    ]);

    const tradingMarketIds = tradingMarketControls
      .filter((marketControl) => marketControl.active)
      .map((marketControl) => marketControl.marketId);
    const nonTradingMarkets = markets
      .filter(
        (market) =>
          market.active &&
          (!tradingMarketIds.includes(market.marketId) || market.marketId === marketId)
      )
      .sort((left, right) => left.name.localeCompare(right.name));

    let tradingMarket;
    if (!isNew) {
      tradingMarket = await getMarketControlByMarket(marketId);
      tradingMarket.eventId = uuid();
    } else {
      tradingMarket = {
        marketId: "",
        halted: true,
        allowPick: false,
        allowAllOrNone: false,
        allowFillOrKill: true,
        withdrawOnCrossTrade: true,
        applyCounterpartyWhitelist: false,
        active: true,
        eventId: uuid(),
        organizationIds: [],
      };
    }

    this.setState({
      markets: nonTradingMarkets,
      data: this.mapToViewModel(tradingMarket),
      organizationIds: tradingMarket.organizationIds || [],
      createdDate: this.formatDisplayDate(tradingMarket.createdDate),
      updatedDate: this.formatDisplayDate(tradingMarket.updatedDate),
      isLoading: false,
    });
  }

  formatDisplayDate(value) {
    if (!value) {
      return "";
    }

    return new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    });
  }

  mapToViewModel(tradingMarket) {
    return {
      marketId: tradingMarket.marketId,
      halted: tradingMarket.halted,
      allowPick: tradingMarket.allowPick,
      allowAllOrNone: tradingMarket.allowAllOrNone,
      allowFillOrKill: tradingMarket.allowFillOrKill,
      withdrawOnCrossTrade: tradingMarket.withdrawOnCrossTrade,
      applyCounterpartyWhitelist: tradingMarket.applyCounterpartyWhitelist,
      active: tradingMarket.active,
      eventId: tradingMarket.eventId,
    };
  }

  handleOrganizationsUpdated = (tradingMarket) => {
    const organizationIds = tradingMarket.organizationIds || [];

    this.setState((currentState) => ({
      organizationIds,
      updatedDate:
        this.formatDisplayDate(tradingMarket.updatedDate) ||
        currentState.updatedDate,
    }));

    this.props.submitCallbackFunction({
      ...this.state.data,
      ...tradingMarket,
      organizationIds,
      name: tradingMarket.name,
    });
  };

  doSubmit = async () => {
    const { t, onRename } = this.props;
    const { isNew, markets, tabId, organizationIds } = this.state;

    try {
      const data = {
        ...this.state.data,
        eventId: uuid(),
      };
      this.setState({ data: data });

      const marketName = markets.find((x) => x.marketId === data.marketId).name;
      const payload = {
        ...data,
        organizationIds: [...organizationIds],
      };

      await insertOrUpdateMarketControl(payload);

      if (!isNew) {
        toast.success(t("TradingMarketUpdated"));
      } else {
        this.setState({ isNew: false, tabId: data.marketId });
        onRename(tabId, data.marketId, marketName);
        toast.success(t("TradingMarketAdded"));
      }

      this.props.submitCallbackFunction({ ...payload, name: marketName });
    } catch (ex) {
      console.log(t("ErrorSaving"), ex);

      if (ex.response && ex.response.status === 400) {
        var errorList = ex.response.data.errors;
        if (errorList) {
          var errorKeys = Object.keys(ex.response.data.errors);
          errorKeys.forEach(function (key) {
            errorList[key].map((error) => {
              toast.error(t("ErrorSaving") + " " + key + ": " + error);
              return null;
            });
          });
        } else {
          toast.error(t("ErrorSaving") + " " + ex.response.data);
        }
        const errors = { ...this.state.errors };
        errors.Name = ex.response.data;
        toast.error(ex.response.data.errors.title);
        this.setState({ errors });
      }
    }
  };

  render() {
    const { isLoading, isNew, createdDate, updatedDate, data } = this.state;

    if (isLoading) return <Loading />;

    const { t, allowEdit } = this.props;
    const marketName =
      this.state.markets.find((market) => market.marketId === data.marketId)?.name ||
      this.props.marketId;

    return (
      <div className="container-fluid">
        <form className="form-inline" onSubmit={this.handleSubmit}>
          <fieldset disabled={allowEdit ? "" : "disabled"}>
            <div>
              {this.renderSwitch("active", t("Active"))}
              {this.renderSelect(
                "marketId",
                t("Market"),
                this.state.markets,
                "marketId",
                "name",
                !this.state.isNew
              )}
              {this.renderSwitch("halted", t("Halted"))}
              {this.renderSwitch("allowPick", t("AllowPick"))}
              {this.renderSwitch("allowAllOrNone", t("AllowAllOrNone"))}
              {this.renderSwitch("allowFillOrKill", t("AllowFillOrKill"))}
              {this.renderSwitch(
                "withdrawOnCrossTrade",
                t("WithdrawOnCrossTrade")
              )}
              {this.renderSwitch(
                "applyCounterpartyWhitelist",
                t("ApplyCounterpartyWhitelist")
              )}
              {allowEdit && this.renderButton(t("Save"))}
              <div className="form-footnote">
                {createdDate !== "Invalid Date" && createdDate && (
                  <div>
                    {t("Created")} {createdDate}
                  </div>
                )}
                {updatedDate !== "Invalid Date" && updatedDate && (
                  <div>
                    {t("Updated")} {updatedDate}
                  </div>
                )}
              </div>
            </div>
          </fieldset>
        </form>
        {!isNew && (
          <div>
            <Tabs
              className="nav-dark nav-tabs-sm"
              defaultActiveKey={1}
              id="tradingmarket-tabs"
              mountOnEnter={true}
              unmountOnExit={true}
            >
              <Tab eventKey={1} title={t("Organizations")}>
                <TradingMarketOrganizations
                  marketId={data.marketId}
                  marketName={marketName}
                  allowEdit={allowEdit}
                  onUpdated={this.handleOrganizationsUpdated}
                />
              </Tab>
              <Tab eventKey={2} title={t("NegotiationMetadata")}>
                <TradingMarketNegotiationMetadata
                  marketId={data.marketId}
                  allowEdit={allowEdit}
                />
              </Tab>
              {/* <Tab eventKey={3} title={t("History")}>
                <TradingMarketHistory marketId={data.marketId} />
              </Tab> */}
            </Tabs>
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation(["trading"])(TradingMarket);
